import {createSelector, createSlice} from '@reduxjs/toolkit';
import {PERMISSIONS} from 'utils/consts';
import {isTrue} from 'utils/helpers';

export const getSafeRedirect = (raw, fallback) => {
  if (!raw) return fallback;
  try {
    const resolved = new URL(raw, window.location.origin);
    return resolved.origin === window.location.origin
      ? resolved.pathname + resolved.search + resolved.hash
      : fallback;
  } catch {
    return fallback;
  }
};

export const ROUTE_PERMISSIONS = {
  audits: 'access_to_audits',
  audit_rules: 'access_to_audits',
  audits_overview: 'access_to_audits',
  audits_issue_overview: 'access_to_audits',
  audits_school_summary: 'access_to_audits',
  data_portal: 'access_to_data_portal',
  data_report: 'access_to_data_reports',
  esoy_it_support: 'can_view_esoy_overview',
  hs_schedule: 'access_to_hs_schedule',
  person: 'access_to_ucpids',
  school_setup: 'access_to_esoy',
  search: 'access_to_search',
  source_manager: 'access_to_source_manager',
  staff_roles_it_support: (p) =>
    p.access_to_staff_account_status && p.can_view_esoy_overview,
  staff_roles: 'access_to_staff_account_status',
  tags_categories: (p) =>
    p.access_to_data_portal_editor ||
    p.access_to_data_reports_editor ||
    p.access_to_source_manager_editor,
};

const RESTRICTED_PATHS = [
  'audits',
  'audit_rules',
  'audits_overview',
  'audits_issue_overview',
  'audits_school_summary',
  'data_portal',
  'data_report',
  'esoy_it_support',
  'hs_schedule',
  'login_done',
  'person',
  'school_setup',
  'search',
  'source_manager',
  'staff_roles_it_support',
  'staff_roles',
  'tags_categories',
];

export const parsePermissions = (data) => {
  // 1. Initial parse based on raw keys
  const permissions = PERMISSIONS.reduce((acc, key) => {
    acc[key] = isTrue(data[key]);
    return acc;
  }, {});

  // 2. Define dependency Map (Editor Key -> Base Key)
  const dependencyMap = {
    access_to_data_portal_editor: 'access_to_data_portal',
    access_to_data_reports_editor: 'access_to_data_reports',
    access_to_source_manager_editor: 'access_to_source_manager',
  };

  // 3. Apply inheritance
  Object.entries(dependencyMap).forEach(([editor, base]) => {
    if (permissions[editor]) {
      permissions[base] = true;
    }
  });

  return permissions;
};

const initialPermissions = Object.fromEntries(
  PERMISSIONS.map((k) => [k, false])
);

const EMPTY_SITE_CODES = Object.freeze([]);

const initialState = {
  permissions: initialPermissions,
  user: null,
  psSiteCodes: EMPTY_SITE_CODES,
  employeeId: null,
  bootstrapped: false,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthData(state, action) {
      const {data, permissions} = action.payload;
      state.permissions = permissions ?? parsePermissions(data);

      // A field absent from the payload preserves the prior value (e.g. a
      // permissions-only /auth/me/ sync); a field explicitly present (even
      // as null) overwrites it.
      if (Object.prototype.hasOwnProperty.call(data, 'ps_site_codes')) {
        state.psSiteCodes = data.ps_site_codes;
      }
      if (Object.prototype.hasOwnProperty.call(data, 'employee_id')) {
        state.employeeId = data.employee_id;
      }
      if (Object.prototype.hasOwnProperty.call(data, 'user_info')) {
        state.user = data.user_info;
      }

      state.bootstrapped = true;
    },
    resetAuth: () => ({
      permissions: {...initialPermissions},
      user: null,
      psSiteCodes: EMPTY_SITE_CODES,
      employeeId: null,
      bootstrapped: true,
    }),
    setBootstrapped(state) {
      state.bootstrapped = true;
    },
  },
});

export const {setAuthData, resetAuth, setBootstrapped} = authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectUser = (state) => state.auth.user;
export const selectIsBootstrapped = (state) =>
  state.auth.bootstrapped || Boolean(state.auth.user);
export const selectPermissions = (state) => state.auth.permissions;
// Persisted state from before ps_site_codes has no psSiteCodes key.
export const selectPsSiteCodes = (state) =>
  state.auth.psSiteCodes ?? EMPTY_SITE_CODES;
export const selectEmployeeId = (state) => state.auth.employeeId;
export const selectIsLoggedIn = (state) => Boolean(state.auth.user);
export const selectPermission = (key) => (state) =>
  state.auth.permissions[key] ?? false;

export const selectHasPermission = createSelector(
  [selectIsLoggedIn, selectPermissions, (_state, route) => route],
  (isLoggedIn, permissions, route) => {
    const path = route.split('/')[1];
    if (!RESTRICTED_PATHS.includes(path)) return true;
    if (!(path in ROUTE_PERMISSIONS)) return isLoggedIn;
    const requirement = ROUTE_PERMISSIONS[path];
    const hasRequirement =
      typeof requirement === 'function'
        ? requirement(permissions)
        : permissions[requirement];
    return isLoggedIn && Boolean(hasRequirement);
  }
);

export const selectUserContext = createSelector(
  [selectUser, selectPsSiteCodes, selectPermissions],
  (user, psSiteCodes, permissions) => {
    if (!user) return {};
    const {_params, ...userInfo} = user;
    return {...userInfo, ps_site_codes: psSiteCodes, permissions};
  }
);
